import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'
import { ControlSize } from 'types/src/controls'
import TextInput from 'ui/src/components/controls/text-input'
import useDebounceValue from 'utils/src/hooks/use-debounce-value'
import useFormattedUrl from 'utils/src/hooks/use-formatted-url'
import FavIcon from './icon'

type Props = {
    autoFocus?: boolean
    children?: ReactNode
    className?: string
    color?: string
    defaultValue?: string
    disabled?: boolean
    placeholder?: string
    selectOnFocus?: boolean
    size: ControlSize
    style?: CSSProperties
    value?: string
    onChange?: (url: string) => void
    onEnterKeyPress?: () => void
}

const UrlInput = ({
    autoFocus,
    children,
    className,
    color = '#fff',
    defaultValue = '',
    disabled,
    placeholder = 'Enter a url',
    selectOnFocus,
    size,
    style,
    value: valueProp,
    onChange: onChangeProp,
    onEnterKeyPress,
}: Props) => {
    const initialValueRef = useRef<string | null>(valueProp || defaultValue)
    const [value, setValue] = useState<string>(initialValueRef.current || '')
    const debouncedValue = useDebounceValue(value, 800)
    const formattedValue = useFormattedUrl(debouncedValue)

    useEffect(() => {
        if (formattedValue !== initialValueRef.current) {
            initialValueRef.current = null
            onChangeProp?.(formattedValue)
        }
    }, [formattedValue])

    useEffect(() => {
        if (typeof valueProp === 'string' && valueProp !== value) {
            setValue(valueProp)
        }
    }, [valueProp])

    return (
        <TextInput
            autoFocus={autoFocus}
            className={className}
            color={color}
            disabled={disabled}
            placeholder={placeholder}
            preview={formattedValue ? <FavIcon size={size} url={formattedValue} /> : undefined}
            selectOnFocus={selectOnFocus}
            size={size}
            style={style}
            value={value}
            onChange={setValue}
            onEnterKeyPress={onEnterKeyPress}
        >
            {children}
        </TextInput>
    )
}

export default UrlInput
